import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type CSSProperties,
  type FormEvent,
  type ReactNode,
} from "react";
import {
  clearToken,
  getToken,
  request,
  setUnauthorizedHandler,
  storeToken,
  type ApiEnvelope,
  type RequestOptions,
} from "@/lib/api";
import { NotificationModal } from "@/components/modals";
import {
  PLATFORM_ACCESS_DENIED_TITLE,
  platformAccessDeniedMessage,
} from "@/utils/accessDenied";

export interface AuthUser {
  id: number | string;
  email: string;
  firstName: string;
  lastName: string;
  preferredName?: string | null;
  avatar?: string | null;
  createdAt?: string;
  updatedAt?: string;
}

export interface AuthResult {
  success: boolean;
  message?: string;
  code?: string;
  currentVersion?: string;
  contextAutoSwitched?: boolean;
  /** UIS signed in, but this membership cannot use AcuityLearn. */
  accessDenied?: boolean;
}

export interface MembershipLearnPreview {
  roles?: AcuityLearnGrants["roles"];
  schoolScope?: AcuityLearnGrants["schoolScope"] | null;
  resolvedSchool?: AcuityLearnGrants["resolvedSchool"] | null;
}

export interface Membership {
  id: string | number;
  group_name?: string;
  groups_periods_title?: string;
  parent_group_name?: string;
  organization_group_name?: string;
  organization_groups_periods_title?: string;
  period_title?: string;
  period_code?: string;
  /** Compact Learn role + school labels for this UIS membership. */
  acuityLearn?: MembershipLearnPreview | null;
}

export interface NavItem {
  id: string;
  label: string;
  path: string;
  order?: number;
}

export interface AcuityLearnGrants {
  platformAccess: boolean;
  roles: {
    superAdmin?: boolean;
    admin?: boolean;
    teacher?: boolean;
    student?: boolean;
    curriculumDeveloper?: boolean;
    counselor?: boolean;
    parent?: boolean;
  };
  capabilities: {
    createSchools?: boolean;
    editSchools?: boolean;
    removeSchools?: boolean;
    addUsers?: boolean;
    /** Gear when adding a person: change which pages/caps that membership receives. */
    overrideAddUserPermissions?: boolean;
    /** See and change a school’s letter-grade scale. */
    editGradeScale?: boolean;
    /** See and change a school’s holidays and other dated days. */
    editSchoolCalendar?: boolean;
    /** Read and reply on office threads (parents writing the school). Office is metadata.thread_kinds. */
    receiveMessages?: boolean;
    sendMessages?: boolean;
    replyMessages?: boolean;
    /** Start a direct message. Directory search needs people.SearchPeople; otherwise an email. */
    sendDm?: boolean;
    /** Reply on a direct message already visible. Does not start one. */
    replyDm?: boolean;
    /** Read other people’s direct-message inboxes. Slice is inboxRoles. Not this person’s own Messages. */
    viewOthersInboxes?: boolean;
    /** Linked guardian or counselor: message a teacher on that student's course who has student chat. */
    sendStudentTeacher?: boolean;
    /** Direct message a person on a linked student's person links. Slice is relationshipTypes. */
    sendStudentRelationship?: boolean;
    /** Link or unlink a parent account to a student. */
    viewPersonLinks?: boolean;
    /** Search the directory and link an existing person as a guardian. Create new does not need this. */
    searchPeople?: boolean;
    personLinks?: boolean;
    /** Add a guardian to a linked student, and update that guardian link. */
    manageStudentGuardianRelationships?: boolean;
    /** A student may add a guardian to themselves only. */
    addSelfGuardianRelationships?: boolean;
    /** Gear on Edit relationship: set a guardian's student and guardian contact doors. School scope applies. */
    grantContactPermissions?: boolean;
    /** See people on a school roster. Slice is viewUserRoles from metadata.view_roles. */
    viewUsers?: boolean;
    /** See a school's course copies, scoped to membership or listed group ids. */
    viewCourses?: boolean;
    /** Open the Catalog slice of master templates. Browse only — does not write masters. */
    viewMasterCatalog?: boolean;
    /** See other people's unowned non-template courses in school scope. */
    viewOthersCourses?: boolean;
    /** Author a course. Does not set is_master by itself. */
    createCourses?: boolean;
    /** Author a catalog template, instantly promote/demote, and apply catalog-upgrade requests. Also opens Catalog. Does not edit existing masters. */
    createMasterCourses?: boolean;
    /** Live-edit existing catalog templates when can_edit is on. Does not write school copies. */
    editMasterCourses?: boolean;
    /** Propose content on a propose-only catalog template. Never live-edits. */
    proposeMasterCourses?: boolean;
    /** Request a shelf course be bumped to the catalog. Apply is CreateMasterCourses, not ReviewProposals. */
    proposeToCatalog?: boolean;
    /** Change Access / Copy / Mod lock on an existing catalog template after create. */
    updateCatalogAccess?: boolean;
    /** Writer door for school copies in scope. Does not write masters — that is editMasterCourses / proposeMasterCourses. */
    editCourses?: boolean;
    /** May teach courses enrolled on as instructor. Still needs an instructor row on that copy. */
    teachEnrolled?: boolean;
    /** Open Teach for school copies in scope without being enrolled as instructor. */
    teachWithoutEnrollment?: boolean;
    /** Live-edit a school copy or master even when mod_lock (and can_edit) would block others. */
    editDespiteModLock?: boolean;
    /** Apply / reject content proposals on non-master courses. Does not review catalog templates. */
    reviewProposals?: boolean;
    /** Apply / reject content proposals on catalog templates. Does not apply Propose to Catalog promotions. */
    reviewCatalogProposals?: boolean;
    /** Open the activity settings gear and change off-by-default fields. */
    overrideActivityDefaults?: boolean;
    /** Staff AI assistant in Course Builder. Writes still need Edit Courses / Teach Enrolled. */
    useBuilderAssistant?: boolean;
    /** Restore this user’s course-builder assistant thread and send prior turns as model context. */
    builderAssistantHistory?: boolean;
    /** Hidden Course Builder deletion tool. Still needs the CourseBreaker query param. */
    courseBreaker?: boolean;
    /** Permanently delete a non-master course (school copy or shelf draft). */
    removeCourses?: boolean;
    /** Permanently delete a catalog template. Does not delete school copies. */
    removeMasterCourses?: boolean;
    /** Copy a catalog master onto a school. Not for My courses shelf drafts — that is placeOnSchool. */
    assignCoursesToSchools?: boolean;
    /** Request a catalog master be copied onto a school in scope. Apply is AssignToSchools. */
    proposeAssignToSchool?: boolean;
    /** Put a non-master shelved course on a school (pin it, or copy so My courses keeps the original). */
    placeOnSchool?: boolean;
    /** Duplicate a school copy that has can_copy. */
    copyCourses?: boolean;
    /** Flip can_edit / can_copy / can_propose / mod_lock on a school copy. */
    setSchoolCopyEdit?: boolean;
    /** Enroll / revoke UGPs on a school-owned course copy. */
    manageEnrollments?: boolean;
    /** Create Core / Concentration rows in UIS taxonomy from Learn. */
    createTaxonomy?: boolean;
    /** Edit Core / Concentration rows in UIS taxonomy from Learn. */
    editTaxonomy?: boolean;
    /** Delete Core / Concentration rows in UIS taxonomy from Learn. */
    removeTaxonomy?: boolean;
    /** See own enrollments and read their published content (student app). */
    viewOwnEnrollments?: boolean;
    /** Open an activity in an enrolled course and have the opening recorded. */
    openActivities?: boolean;
    /** Submit answers for a graded activity in an enrolled course. */
    submitWork?: boolean;
    /** Reply on the public work thread for the holder's own progress. */
    replyOnProgress?: boolean;
    /** Parent: read the student + teacher chat on a linked student's work. */
    viewStudentChat?: boolean;
    /** Parent: post in that chat (still follows the teacher's chat switch). */
    messageInStudentChat?: boolean;
    /** List the classes a linked student is enrolled in. */
    viewStudentsCourseEnrollments?: boolean;
    /** Open a linked student's class outline and, for now, that student's work. */
    viewStudentsCourseContent?: boolean;
    /** One student-and-staff thread per student membership, plus private notes on that student. */
    studentStaffChat?: boolean;
    /** One staff-only thread per student membership, plus private notes on that student. */
    staffStudentChat?: boolean;
    /** Signed-in person: update their own contact. A filled sign-in email or phone still needs Edit user data. */
    updateOwnContact?: boolean;
    /** Overwrite a sign-in email or phone inside this school scope. Does not let anyone else update the platform owner. */
    editUserData?: boolean;
    /** Same overwrite when the person also has a context outside this school scope. Still cannot change the platform owner. */
    editUserDataMaster?: boolean;
    /** Staff: see contact for people in this membership's school scope, from the people view. */
    viewContact?: boolean;
    /** Staff: update contact for people in this membership's school scope. Includes view. */
    updateContact?: boolean;
    /** Parent: see a linked student's contact. */
    viewStudentContact?: boolean;
    /** Parent, declared only: update a linked student's contact info. Includes view. */
    updateStudentContact?: boolean;
    /** Parent: see the other guardians linked to their student. */
    viewGuardianContact?: boolean;
    /** Parent, declared only: update another guardian linked to their student. Includes view. */
    updateGuardianContact?: boolean;
    /** Parent, declared only: reset a linked student's password. */
    resetStudentPassword?: boolean;
    /** Parent, declared only: message a linked student's school admins. */
    contactSchoolAdmin?: boolean;
    messageStudentsOffice?: boolean;
    /** Post a class note on a school-owned course copy. */
    postAnnouncements?: boolean;
    viewLibrary?: boolean;
    /** See other people's Library sources in school scope. */
    viewOthersSources?: boolean;
    /** Author a new Library source as a draft. Does not propose and does not publish. */
    addSources?: boolean;
    /** Live-edit / publish existing Library sources. Does not create new rows. */
    editSources?: boolean;
    proposeSources?: boolean;
    reviewSources?: boolean;
    removeSources?: boolean;
    analyzeSources?: boolean;
    manageApprovedCatalogs?: boolean;
    scopeGlobal?: boolean;
    scopeListedGroups?: boolean;
    /** events.ManageEvents. Verbs are eventActions. */
    manageEvents?: boolean;
    /** events.ManageAttendance. Verbs are attendanceActions. */
    manageAttendance?: boolean;
    /** events.Respond. Own RSVP only. */
    respondToEvents?: boolean;
  };
    /** people.PersonLinks metadata.link_types. Guardians and counselors are on when the field is omitted. parents is a legacy alias of guardians. */
    linkPersonTypes?: {
      parents?: boolean;
      guardians?: boolean;
      siblings?: boolean;
      counselors?: boolean;
    };
    addUserRoles?: {
      superAdmin?: boolean;
      admin?: boolean;
      teacher?: boolean;
      student?: boolean;
      curriculumDeveloper?: boolean;
      counselor?: boolean;
      parent?: boolean;
    };
    viewUserRoles?: {
      superAdmin?: boolean;
      admin?: boolean;
      teacher?: boolean;
      student?: boolean;
      curriculumDeveloper?: boolean;
      counselor?: boolean;
      parent?: boolean;
    };
    /** dm.SendStudentRelationship metadata.relationship_types. Omitted or empty means none. */
    relationshipTypes?: {
      guardian?: boolean;
      sibling?: boolean;
      teacher_of_record?: boolean;
      counselor?: boolean;
      supervisor?: boolean;
    };
    /** dm.ViewOthersInboxes metadata.inbox_roles. Omitted means none. */
    inboxRoles?: {
      superAdmin?: boolean;
      admin?: boolean;
      teacher?: boolean;
      student?: boolean;
      curriculumDeveloper?: boolean;
      counselor?: boolean;
      parent?: boolean;
    };
    /** UIS course.TeachEnrolled metadata.instructor_kit. All four default on when the cap is allowed and metadata is omitted. */
    instructorKit?: {
      proposeActivities?: boolean;
      editActivities?: boolean;
      gradeActivities?: boolean;
      overrideActivities?: boolean;
    };
    /** messages.ReceiveMessages metadata.thread_kinds. Omitted means office. Support is not stored yet. */
    messageKinds?: {
      office?: boolean;
    };
    /** Teach Without Enrollment kit. All five default on when the cap is allowed and metadata is omitted. */
    supervisorKit?: {
      viewActivities?: boolean;
      gradeActivities?: boolean;
      overrideActivities?: boolean;
      studentProgressChat?: boolean;
      staffProgressChat?: boolean;
    };
    /** page_access.grade-rubrics metadata.rubric_actions. All three default on when the page is allowed and the field is omitted. */
    gradeRubricActions?: {
      create?: boolean;
      edit?: boolean;
      delete?: boolean;
    };
    /** events.ManageEvents metadata.event_actions. All three default on when the cap is allowed and the field is omitted. */
    eventActions?: {
      insert?: boolean;
      edit?: boolean;
      remove?: boolean;
    };
    /** events.ManageAttendance metadata.attendance_actions. Both default on when the cap is allowed and the field is omitted. */
    attendanceActions?: {
      insert?: boolean;
      update?: boolean;
    };
    /** UIS library.AddSources / EditSources / ProposeSources metadata.source_kit. All kinds + both actions default on when omitted. */
    librarySourceKit?: {
      document?: boolean;
      image?: boolean;
      audio?: boolean;
      video?: boolean;
      url?: boolean;
      replaceFile?: boolean;
      editAnalysis?: boolean;
      maxUploadBytes?: number;
      documentMaxBytes?: number;
      mediaMaxBytes?: number;
    };
  pageAccess: Record<string, boolean>;
  nav: NavItem[];
  roleTemplatesApplied?: boolean;
  defaultHomePath?: string;
  schoolScope?: { type: string; groupIds?: string[] };
  resolvedSchool?: { groupId: string; groupName?: string | null } | null;
}

export interface AcuityLearnSession {
  success?: boolean;
  hasAccess?: boolean;
  requiresAuth?: boolean;
  message?: string;
  denialCode?: string;
  contextAutoSwitched?: boolean;
  user?: AuthUser;
  currentMembership?: Membership | null;
  switchableMemberships?: Membership[];
  acuityLearn?: AcuityLearnGrants;
}

export interface AuthContextValue {
  user: AuthUser | null;
  token: string | null;
  session: AcuityLearnSession | null;
  loading: boolean;
  isAuthenticated: boolean;
  hasAccess: boolean;
  sessionExpired: boolean;
  roles: AcuityLearnGrants["roles"];
  capabilities: AcuityLearnGrants["capabilities"];
  addUserRoles: NonNullable<AcuityLearnGrants["addUserRoles"]>;
  viewUserRoles: NonNullable<AcuityLearnGrants["viewUserRoles"]>;
  inboxRoles: NonNullable<AcuityLearnGrants["inboxRoles"]>;
  instructorKit: NonNullable<AcuityLearnGrants["instructorKit"]>;
  supervisorKit: NonNullable<AcuityLearnGrants["supervisorKit"]>;
  librarySourceKit: NonNullable<AcuityLearnGrants["librarySourceKit"]>;
  pageAccess: Record<string, boolean>;
  nav: NavItem[];
  switchableMemberships: Membership[];
  currentMembership: Membership | null;
  login: (email: string, password: string) => Promise<AuthResult>;
  loginWithToken: (token: string) => Promise<AcuityLearnSession | null>;
  register: (
    email: string,
    password: string,
    firstName: string,
    lastName: string,
  ) => Promise<AuthResult>;
  switchMembership: (ugpId: string | number) => Promise<AcuityLearnSession>;
  logout: () => void;
  updateProfile: (firstName: string, lastName: string) => Promise<AuthResult>;
  changePassword: (currentPassword: string, newPassword: string) => Promise<AuthResult>;
  fetchWithAuth: (path: string, init?: RequestInit) => Promise<Response>;
}

/** The auth routes add `currentVersion` alongside the standard envelope. */
interface AuthEnvelope<T = unknown> extends ApiEnvelope<T> {
  currentVersion?: string;
}

const NETWORK_ERROR: AuthResult = { success: false, message: "Network error. Please try again." };

const AuthContext = createContext<AuthContextValue | null>(null);

export const useAuth = (): AuthContextValue => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within an AuthProvider");
  return context;
};

/** Returns `null` when the server answered with something other than JSON. */
async function readJson<T>(response: Response): Promise<(AuthEnvelope<T> & T) | null> {
  if (!(response.headers.get("content-type") ?? "").includes("application/json")) return null;
  return (await response.json()) as AuthEnvelope<T> & T;
}

const jsonRequest = (
  path: string,
  method: string,
  body: unknown,
  options?: RequestOptions,
): Promise<Response> =>
  request(
    path,
    { method, headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) },
    options,
  );

function normalizeUser(raw?: AuthUser | null): AuthUser | null {
  if (!raw) return null;
  const record = raw as AuthUser & { first_name?: string; last_name?: string };
  return {
    ...raw,
    firstName: raw.firstName || record.first_name || "",
    lastName: raw.lastName || record.last_name || "",
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<AcuityLearnSession | null>(null);
  const [token, setToken] = useState<string | null>(() => getToken());
  const [loading, setLoading] = useState(true);
  const [sessionExpired, setSessionExpired] = useState(false);

  const [reauthEmail, setReauthEmail] = useState("");
  const [reauthPassword, setReauthPassword] = useState("");
  const [reauthError, setReauthError] = useState("");
  const [reauthLoading, setReauthLoading] = useState(false);
  const [platformDeniedOpen, setPlatformDeniedOpen] = useState(false);
  const [platformDeniedMessage, setPlatformDeniedMessage] = useState(
    platformAccessDeniedMessage(),
  );

  const user = normalizeUser(session?.user ?? null);

  useEffect(() => {
    setUnauthorizedHandler(() => setSessionExpired(true));
    return () => setUnauthorizedHandler(null);
  }, []);

  const refreshSession = useCallback(async (): Promise<AcuityLearnSession | null> => {
    const current = getToken();
    if (!current) {
      setSession(null);
      setToken(null);
      setLoading(false);
      return null;
    }

    try {
      const response = await request("/api/auth/me", undefined, {
        promptOnUnauthorized: false,
      });
      const payload = await readJson<AcuityLearnSession>(response);

      if (response.status === 401 || payload?.requiresAuth || payload?.success === false) {
        clearToken();
        setToken(null);
        setSession(null);
        setLoading(false);
        return null;
      }

      if (payload?.user) {
        if (!payload.hasAccess) {
          clearToken();
          setToken(null);
          setSession(null);
          setLoading(false);
          setPlatformDeniedMessage(platformAccessDeniedMessage(payload.denialCode));
          setPlatformDeniedOpen(true);
          return payload;
        }
        setSession(payload);
        setToken(current);
        setLoading(false);
        return payload;
      }

      clearToken();
      setToken(null);
      setSession(null);
      setLoading(false);
      return null;
    } catch {
      clearToken();
      setToken(null);
      setSession(null);
      setLoading(false);
      return null;
    }
  }, []);

  useEffect(() => {
    void refreshSession();
  }, [refreshSession]);

  const loginWithToken = useCallback(
    async (nextToken: string): Promise<AcuityLearnSession | null> => {
      storeToken(nextToken);
      setToken(nextToken);
      setLoading(true);
      return refreshSession();
    },
    [refreshSession],
  );

  const login = useCallback(
    async (email: string, password: string): Promise<AuthResult> => {
      try {
        const response = await jsonRequest("/api/auth/login", "POST", { email, password }, {
          promptOnUnauthorized: false,
        });
        const payload = await readJson<{ data?: { token?: string; user?: AuthUser }; token?: string }>(
          response,
        );

        const tokenValue = payload?.data?.token || payload?.token;
        if (!response.ok || payload?.success === false || !tokenValue) {
          const fallback =
            response.status === 503
              ? "AcuityLearn is not connected to UIS. Set UIS_API_URL on the server."
              : response.status === 403
                ? "Unable to load your UIS role. Contact support."
                : "Login failed. Please check your credentials or try again.";
          return { success: false, message: payload?.message ?? fallback };
        }

        const next = await loginWithToken(tokenValue);
        if (!next?.user) {
          clearToken();
          setToken(null);
          setSession(null);
          const message = "Your login worked, but AcuityLearn could not load your session.";
          setPlatformDeniedMessage(message);
          setPlatformDeniedOpen(true);
          return { success: false, accessDenied: true, message };
        }
        if (!next.hasAccess) {
          const message = platformAccessDeniedMessage(next.denialCode);
          return {
            success: false,
            accessDenied: true,
            code: next.denialCode,
            message,
          };
        }

        return {
          success: true,
          message: payload?.message,
          contextAutoSwitched: Boolean(next.contextAutoSwitched),
        };
      } catch {
        return NETWORK_ERROR;
      }
    },
    [loginWithToken],
  );

  const register = useCallback(async (): Promise<AuthResult> => {
    return {
      success: false,
      code: "REGISTRATION_DISABLED",
      message: "Create accounts in UIS. AcuityLearn signs in through UIS_API_URL.",
    };
  }, []);

  const logout = useCallback(() => {
    setSession(null);
    setToken(null);
    clearToken();
  }, []);

  const switchMembership = useCallback(async (ugpId: string | number): Promise<AcuityLearnSession> => {
    const response = await jsonRequest("/api/auth/switch-membership", "PUT", { ugpId });
    const payload = await readJson<AcuityLearnSession>(response);
    if (!payload?.user) {
      throw new Error(payload?.message || "Failed to switch membership");
    }
    setSession(payload);
    return payload;
  }, []);

  const fetchWithAuth = useCallback((path: string, init?: RequestInit) => request(path, init), []);

  const updateProfile = useCallback(
    async (firstName: string, lastName: string): Promise<AuthResult> => {
      try {
        const response = await jsonRequest("/api/auth/profile", "PUT", { firstName, lastName });
        const payload = await readJson<{ user: AuthUser }>(response);

        if (payload?.success && payload.data) {
          await refreshSession();
          return { success: true, message: payload.message };
        }

        return {
          success: false,
          message: payload?.message,
          code: payload?.code,
          currentVersion: payload?.currentVersion,
        };
      } catch {
        return NETWORK_ERROR;
      }
    },
    [refreshSession],
  );

  const changePassword = useCallback(
    async (currentPassword: string, newPassword: string): Promise<AuthResult> => {
      try {
        const response = await jsonRequest("/api/auth/change-password", "PUT", {
          currentPassword,
          newPassword,
        });
        const payload = await readJson<unknown>(response);

        if (payload?.success) return { success: true, message: payload.message };

        return {
          success: false,
          message: payload?.message,
          code: payload?.code,
          currentVersion: payload?.currentVersion,
        };
      } catch {
        return NETWORK_ERROR;
      }
    },
    [],
  );

  const grants = session?.acuityLearn;

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      token,
      session,
      loading,
      isAuthenticated: Boolean(token && user),
      hasAccess: Boolean(session?.hasAccess),
      sessionExpired,
      roles: grants?.roles || {},
      capabilities: grants?.capabilities || {},
      addUserRoles: grants?.addUserRoles || {},
      viewUserRoles: grants?.viewUserRoles || {},
      inboxRoles: grants?.inboxRoles || {},
      instructorKit: grants?.instructorKit || {},
      supervisorKit: grants?.supervisorKit || {},
      librarySourceKit: grants?.librarySourceKit || {},
      pageAccess: grants?.pageAccess || {},
      nav: grants?.nav || [],
      switchableMemberships: session?.switchableMemberships || [],
      currentMembership: session?.currentMembership || null,
      login,
      loginWithToken,
      register,
      switchMembership,
      logout,
      updateProfile,
      changePassword,
      fetchWithAuth,
    }),
    [
      user,
      token,
      session,
      loading,
      sessionExpired,
      grants,
      login,
      loginWithToken,
      register,
      switchMembership,
      logout,
      updateProfile,
      changePassword,
      fetchWithAuth,
    ],
  );

  const handleReauth = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setReauthError("");
    setReauthLoading(true);

    const result = await login(reauthEmail, reauthPassword);
    setReauthLoading(false);

    if (result.success) {
      setSessionExpired(false);
      setReauthEmail("");
      setReauthPassword("");
    } else if (result.accessDenied) {
      setReauthError("");
    } else {
      setReauthError(result.message ?? "Login failed. Please try again.");
    }
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <NotificationModal
        isOpen={platformDeniedOpen}
        type="error"
        title={PLATFORM_ACCESS_DENIED_TITLE}
        message={platformDeniedMessage}
        overlay
        overlayClose
        escapeClose
        showOkButton
        okButtonText="OK"
        zIndex={2100}
        onClose={() => setPlatformDeniedOpen(false)}
      />
      {sessionExpired && (
        <div style={modalStyles.backdrop}>
          <div style={modalStyles.modal}>
            <h3 style={modalStyles.title}>Session expired</h3>
            <p style={modalStyles.text}>
              Please log in again to continue. Your current page and data are preserved.
            </p>
            {reauthError && <div style={modalStyles.error}>{reauthError}</div>}
            <form onSubmit={handleReauth} style={modalStyles.form}>
              <label style={modalStyles.label}>Email</label>
              <input
                type="email"
                value={reauthEmail}
                onChange={(event) => setReauthEmail(event.target.value)}
                required
                style={modalStyles.input}
                autoFocus
              />
              <label style={modalStyles.label}>Password</label>
              <input
                type="password"
                value={reauthPassword}
                onChange={(event) => setReauthPassword(event.target.value)}
                required
                style={modalStyles.input}
              />
              <div style={modalStyles.actions}>
                <button type="submit" disabled={reauthLoading} style={modalStyles.primaryBtn}>
                  {reauthLoading ? "Logging in…" : "Log In"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AuthContext.Provider>
  );
}

const modalStyles = {
  backdrop: {
    position: "fixed",
    inset: 0,
    background: "rgba(15, 23, 42, 0.28)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 2000,
  },
  modal: {
    background: "var(--surface-1)",
    border: "1px solid var(--line)",
    borderRadius: "var(--r-lg)",
    padding: "14px",
    maxWidth: "360px",
    width: "90%",
    boxShadow: "var(--elev-3)",
  },
  title: { margin: "0 0 4px", color: "var(--ink)", fontSize: "0.875rem", fontWeight: 600 },
  text: { margin: "0 0 12px", color: "var(--ink-muted)", fontSize: "0.8125rem" },
  actions: { display: "flex", gap: "6px", justifyContent: "flex-end" },
  primaryBtn: {
    background: "var(--ink)",
    color: "var(--surface-1)",
    padding: "6px 10px",
    border: "none",
    borderRadius: "var(--r-xs)",
    cursor: "pointer",
    fontSize: "0.75rem",
  },
  form: { display: "grid", gap: "8px" },
  label: { fontSize: "0.75rem", color: "var(--ink-muted)" },
  input: {
    padding: "7px 10px",
    border: "1px solid var(--line)",
    borderRadius: "var(--r-xs)",
    outline: "none",
    fontSize: "0.8125rem",
  },
  error: {
    background: "var(--surface-2)",
    border: "1px solid var(--line)",
    color: "var(--ink)",
    padding: "6px 8px",
    borderRadius: "var(--r-xs)",
    marginBottom: "4px",
    fontSize: "0.75rem",
  },
} satisfies Record<string, CSSProperties>;
